/**
 * @deprecated Use GameAccordion instead. Kept as LegacyOpenOrdersPanel for rollback.
 *
 * OpenOrdersPanel - Displays open orders for the NBA Value Dashboard
 *
 * Shows a table of open orders with the ability to cancel individual orders
 * or kill all orders at once. Sortable columns, USD cost display.
 */

import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { PolyOpenOrder } from '@/lib/polymarket/types';
import { formatPriceCents } from '@/lib/nbaConsolidated/format';
import { computeCost } from '@/lib/orderMath';
import { derivePositionLabel } from '@/lib/nbaConsolidated/positionLabel';
import type { VelocityResult } from '@/lib/queueVelocity';
import type { Position } from '@/types';
import { Money } from '@/components/atoms/Money';
import { TickerDisplay } from '@/components/atoms/TickerDisplay';
import { Th, Td } from '@/components/atoms/TableCells';
import { ConfirmModal } from '@/components/atoms/Modal';
import { DecreaseOrderQuantityEditor } from '@/components/molecules/DecreaseOrderQuantityEditor';

type SortKey = 'market' | 'side' | 'qty' | 'price' | 'queue' | 'cost';
type PolySortKey = 'market' | 'side' | 'remaining' | 'price' | 'value';
type SortDir = 'asc' | 'desc';

interface OpenOrdersPanelProps {
  kalshiOrders: KalshiOrder[];
  polyOrders: PolyOpenOrder[];
  polyStatus?: 'disconnected' | 'connected' | 'error';
  /** Optional mapping from ticker -> market title (displayed above ticker) */
  titleByTicker?: Record<string, string>;
  /** Optional mapping from Polymarket token ID (assetId) -> human-readable label */
  polyTokenLabels?: Record<string, string>;
  /** Optional mapping from ticker -> { yes/no outcome label } for informative side labels */
  outcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  /** Velocity data per order ID */
  velocityByOrderId?: Record<string, VelocityResult>;
  /** Absolute queue depth per order ID */
  absoluteQueueByOrderId?: Record<string, number>;
  /** Open positions for net position display */
  positions?: Position[];
  onCancelOrder?: (orderId: string) => void;
  onDecreaseOrder?: (orderId: string, nextQuantity: number) => Promise<void> | void;
  onKillAll?: () => void;
  onCancelPolyOrder?: (orderId: string) => void;
  onKillAllPolyOrders?: () => void;
  className?: string;
}

/** Helper: extract sort value from a Kalshi order */
function getKalshiSortValue(order: KalshiOrder, key: SortKey): string | number {
  const px = order.side === 'yes' ? order.yes_price : order.no_price;
  switch (key) {
    case 'market':
      return order.ticker;
    case 'side':
      return order.side;
    case 'qty':
      return order.remaining_count;
    case 'price':
      return px ?? 0;
    case 'queue':
      return order.queue_position ?? Infinity;
    case 'cost': {
      // Cost = remaining × price_cents / 100
      const costPerContract = order.side === 'yes' ? (px ?? 0) : 100 - (px ?? 0);
      return (costPerContract * order.remaining_count) / 100;
    }
    default:
      return 0;
  }
}

/** Helper: extract sort value from a Polymarket order */
function getPolySortValue(
  order: PolyOpenOrder,
  key: PolySortKey,
  label: string | undefined
): string | number {
  const remaining = Math.max(0, (order.originalSize ?? 0) - (order.sizeMatched ?? 0));
  switch (key) {
    case 'market':
      return label ?? order.assetId;
    case 'side':
      return String(order.side);
    case 'remaining':
      return remaining;
    case 'price':
      return Number.isFinite(order.price) ? order.price : 0;
    case 'value':
      return remaining * (Number.isFinite(order.price) ? order.price : 0);
    default:
      return 0;
  }
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  className,
}: {
  label: string;
  sortKey: string;
  currentKey: string | null;
  currentDir: SortDir;
  onSort: (key: string) => void;
  className?: string;
}) {
  const isActive = currentKey === sortKey;

  return (
    <Th className={cn('cursor-pointer select-none', className)}>
      <button
        className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {isActive ? (
          currentDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </Th>
  );
}

export function LegacyOpenOrdersPanel({
  kalshiOrders,
  polyOrders,
  polyStatus = 'disconnected',
  titleByTicker,
  polyTokenLabels,
  outcomeByTicker,
  velocityByOrderId,
  absoluteQueueByOrderId,
  positions,
  onCancelOrder,
  onDecreaseOrder,
  onKillAll,
  onCancelPolyOrder,
  onKillAllPolyOrders,
  className,
}: OpenOrdersPanelProps) {
  const [showAllRows, setShowAllRows] = useState(false);
  const [showKillAllConfirm, setShowKillAllConfirm] = useState(false);
  const [showPolyKillAllConfirm, setShowPolyKillAllConfirm] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>('queue');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [polySortKey, setPolySortKey] = useState<PolySortKey | null>('price');
  const [polySortDir, setPolySortDir] = useState<SortDir>('desc');
  const maxRows = 10;

  // Build position lookup keyed by "ticker:side"
  const positionByKey = useMemo(() => {
    const map = new Map<string, Position>();
    for (const pos of positions ?? []) {
      map.set(`${pos.ticker}:${pos.side}`, pos);
    }
    return map;
  }, [positions]);

  // Queue depth loading: true when orders exist but no depth data yet (initial fetch pending)
  const isQueueDepthLoading =
    kalshiOrders.length > 0 && Object.keys(absoluteQueueByOrderId ?? {}).length === 0;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key as SortKey);
      setSortDir('asc');
    }
  };

  const handlePolySort = (key: string) => {
    if (polySortKey === key) {
      setPolySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setPolySortKey(key as PolySortKey);
      setPolySortDir('asc');
    }
  };

  const sortedKalshiOrders = useMemo(() => {
    if (!sortKey) return kalshiOrders;
    return [...kalshiOrders].sort((a, b) => {
      const aVal = getKalshiSortValue(a, sortKey);
      const bVal = getKalshiSortValue(b, sortKey);
      const cmp =
        typeof aVal === 'string'
          ? aVal.localeCompare(bVal as string)
          : (aVal as number) - (bVal as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [kalshiOrders, sortKey, sortDir]);

  const visibleKalshiOrders = useMemo(() => {
    if (showAllRows) return sortedKalshiOrders;
    return sortedKalshiOrders.slice(0, maxRows);
  }, [sortedKalshiOrders, showAllRows]);

  const sortedPolyOrders = useMemo(() => {
    if (!polySortKey) return polyOrders;
    return [...polyOrders].sort((a, b) => {
      const aVal = getPolySortValue(a, polySortKey, polyTokenLabels?.[a.assetId]);
      const bVal = getPolySortValue(b, polySortKey, polyTokenLabels?.[b.assetId]);
      const cmp =
        typeof aVal === 'string'
          ? aVal.localeCompare(bVal as string)
          : (aVal as number) - (bVal as number);
      return polySortDir === 'asc' ? cmp : -cmp;
    });
  }, [polyOrders, polySortKey, polySortDir, polyTokenLabels]);

  const visiblePolyOrders = useMemo(() => {
    if (showAllRows) return sortedPolyOrders;
    // If Kalshi took some slots, reduce poly slots
    const remainingSlots = Math.max(0, maxRows - kalshiOrders.length);
    return sortedPolyOrders.slice(0, remainingSlots);
  }, [sortedPolyOrders, kalshiOrders.length, showAllRows]);

  const totalOrders = kalshiOrders.length + polyOrders.length;

  return (
    <div className={cn('bg-card border-border rounded-lg border', className)}>
      {/* Header - clean section title */}
      <div className="border-border flex items-center justify-between border-b px-4 py-4">
        <h2 className="text-foreground text-lg font-semibold">Open Orders</h2>
        {kalshiOrders.length > 0 && onKillAll && (
          <button
            onClick={() => setShowKillAllConfirm(true)}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-md px-4 py-2 text-xs font-medium uppercase tracking-wider shadow-sm transition-colors"
          >
            Kill All
          </button>
        )}

        {/* Kill All Confirmation Modal */}
        <ConfirmModal
          isOpen={showKillAllConfirm}
          onClose={() => setShowKillAllConfirm(false)}
          onConfirm={() => onKillAll?.()}
          title="Cancel All Orders"
          message={`Are you sure you want to cancel all ${kalshiOrders.length} open order${kalshiOrders.length === 1 ? '' : 's'}? This action cannot be undone.`}
          confirmLabel="Kill All"
          cancelLabel="Keep Orders"
          variant="danger"
        />
      </div>

      {/* Kalshi */}
      <div className="border-border/50 border-b p-4">
        <div className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wider">
          Kalshi ({kalshiOrders.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-border border-b">
              <tr>
                <SortableHeader
                  label="Market"
                  sortKey="market"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Side"
                  sortKey="side"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Qty"
                  sortKey="qty"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-right"
                />
                <SortableHeader
                  label="Price"
                  sortKey="price"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-right"
                />
                <SortableHeader
                  label="Cost"
                  sortKey="cost"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-right"
                />
                <SortableHeader
                  label="Q @ Price"
                  sortKey="queue"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-right"
                />
                <Th className="text-right">Velocity</Th>
                <Th className="text-right">Abs Q</Th>
                <Th className="text-right">Net Pos</Th>
                <Th className="text-right">Avg Fill</Th>
                <Th className="text-center"> </Th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {kalshiOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="text-muted-foreground px-3 py-6 text-center"
                  >
                    No Kalshi open orders
                  </td>
                </tr>
              ) : (
                visibleKalshiOrders.map((o, rowIndex) => {
                  const px = o.side === 'yes' ? o.yes_price : o.no_price;
                  const title = titleByTicker?.[o.ticker];
                  const orderCost =
                    px != null ? computeCost(px, o.remaining_count, o.side, 'kalshi', false) : null;
                  const zebraStyle =
                    rowIndex % 2 === 1
                      ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
                      : undefined;
                  return (
                    <tr
                      key={o.order_id}
                      className="hover:bg-accent/50 transition-colors"
                      style={zebraStyle}
                    >
                      <Td>
                        <TickerDisplay
                          ticker={o.ticker}
                          title={title}
                          variant="compact"
                        />
                      </Td>
                      <Td>
                        <span
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-medium',
                            o.side === 'yes'
                              ? 'bg-positive/20 text-positive'
                              : 'bg-negative/20 text-negative'
                          )}
                        >
                          {derivePositionLabel(o.ticker, o.side, outcomeByTicker)}
                        </span>
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {onDecreaseOrder ? (
                          <DecreaseOrderQuantityEditor
                            currentQuantity={o.remaining_count}
                            onSubmit={async (nextQuantity) => {
                              await onDecreaseOrder(o.order_id, nextQuantity);
                            }}
                          />
                        ) : (
                          o.remaining_count.toLocaleString()
                        )}
                        {orderCost && (
                          <div className="text-muted-foreground text-[10px]">
                            <Money
                              value={orderCost.costDollars}
                              className="text-[10px]"
                            />
                          </div>
                        )}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {formatPriceCents(px ?? null)}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {orderCost ? (
                          <Money
                            value={orderCost.costDollars}
                            className="text-xs"
                          />
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td className="text-muted-foreground text-right font-mono text-xs">
                        {o.queue_position != null && px != null
                          ? `${o.queue_position.toLocaleString()} @ ${px}¢`
                          : (o.queue_position?.toLocaleString() ?? '—')}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {(() => {
                          const v = velocityByOrderId?.[o.order_id];
                          if (!v) return <span className="text-muted-foreground">—</span>;
                          const absV = Math.abs(v.velocity);
                          if (absV < 0.1)
                            return <span className="text-muted-foreground">· 0.0</span>;
                          const arrow = v.direction === 'advancing' ? '↑' : '↓';
                          const color =
                            absV >= 20
                              ? 'text-destructive'
                              : absV >= 5
                                ? 'text-yellow-400'
                                : 'text-muted-foreground';
                          return (
                            <span className={color}>
                              {arrow} {absV.toFixed(1)}/s
                            </span>
                          );
                        })()}
                      </Td>
                      <Td className="text-muted-foreground text-right font-mono text-xs">
                        {absoluteQueueByOrderId?.[o.order_id] != null ? (
                          absoluteQueueByOrderId[o.order_id].toLocaleString()
                        ) : isQueueDepthLoading ? (
                          <span className="animate-pulse">···</span>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {(() => {
                          const pos = positionByKey.get(`${o.ticker}:${o.side}`);
                          if (!pos || pos.quantity === 0)
                            return <span className="text-muted-foreground">—</span>;
                          return pos.quantity.toLocaleString();
                        })()}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        {(() => {
                          const pos = positionByKey.get(`${o.ticker}:${o.side}`);
                          if (!pos || pos.quantity === 0)
                            return <span className="text-muted-foreground">—</span>;
                          return `${(pos.averagePrice * 100).toFixed(0)}¢`;
                        })()}
                      </Td>
                      <Td className="text-center">
                        {onCancelOrder && (
                          <button
                            onClick={() => onCancelOrder(o.order_id)}
                            className="hover:bg-destructive/20 hover:text-destructive rounded p-1 transition-colors"
                            title="Cancel Kalshi order"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {kalshiOrders.length > 0 && (
              <tfoot className="bg-muted/30 border-border border-t">
                <tr>
                  <Td
                    className="text-muted-foreground text-xs font-semibold"
                    colSpan={2}
                  >
                    Totals
                  </Td>
                  <Td className="text-right font-mono text-xs font-semibold">
                    {kalshiOrders.reduce((s, o) => s + o.remaining_count, 0).toLocaleString()}
                    <div className="text-muted-foreground text-[10px]">
                      <Money
                        value={kalshiOrders.reduce((s, o) => {
                          const px = o.side === 'yes' ? o.yes_price : o.no_price;
                          if (px == null) return s;
                          return (
                            s +
                            computeCost(px, o.remaining_count, o.side, 'kalshi', false).costDollars
                          );
                        }, 0)}
                        className="text-[10px]"
                      />
                    </div>
                  </Td>
                  <Td colSpan={8}> </Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Polymarket */}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
            Polymarket ({polyOrders.length}) · {polyStatus}
          </div>
          {polyOrders.length > 0 && onKillAllPolyOrders && (
            <button
              onClick={() => setShowPolyKillAllConfirm(true)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-md px-4 py-2 text-xs font-medium uppercase tracking-wider shadow-sm transition-colors"
            >
              Kill All
            </button>
          )}
        </div>

        {/* Poly Kill All Confirmation Modal */}
        <ConfirmModal
          isOpen={showPolyKillAllConfirm}
          onClose={() => setShowPolyKillAllConfirm(false)}
          onConfirm={() => onKillAllPolyOrders?.()}
          title="Cancel All Polymarket Orders"
          message={`Are you sure you want to cancel all ${polyOrders.length} Polymarket open order${polyOrders.length === 1 ? '' : 's'}? This action cannot be undone.`}
          confirmLabel="Kill All"
          cancelLabel="Keep Orders"
          variant="danger"
        />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-border border-b">
              <tr>
                <SortableHeader
                  label="Market"
                  sortKey="market"
                  currentKey={polySortKey}
                  currentDir={polySortDir}
                  onSort={handlePolySort}
                />
                <SortableHeader
                  label="Side"
                  sortKey="side"
                  currentKey={polySortKey}
                  currentDir={polySortDir}
                  onSort={handlePolySort}
                />
                <SortableHeader
                  label="Remaining"
                  sortKey="remaining"
                  currentKey={polySortKey}
                  currentDir={polySortDir}
                  onSort={handlePolySort}
                  className="text-right"
                />
                <SortableHeader
                  label="Price"
                  sortKey="price"
                  currentKey={polySortKey}
                  currentDir={polySortDir}
                  onSort={handlePolySort}
                  className="text-right"
                />
                <SortableHeader
                  label="Value"
                  sortKey="value"
                  currentKey={polySortKey}
                  currentDir={polySortDir}
                  onSort={handlePolySort}
                  className="text-right"
                />
                {onCancelPolyOrder && <Th className="text-center"> </Th>}
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {polyOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan={onCancelPolyOrder ? 6 : 5}
                    className="text-muted-foreground px-3 py-6 text-center"
                  >
                    No Polymarket open orders
                  </td>
                </tr>
              ) : (
                visiblePolyOrders.map((o, rowIndex) => {
                  const remaining = Math.max(0, (o.originalSize ?? 0) - (o.sizeMatched ?? 0));
                  const label = polyTokenLabels?.[o.assetId];
                  const valueDollars = remaining * (Number.isFinite(o.price) ? o.price : 0);
                  const zebraStyle =
                    rowIndex % 2 === 1
                      ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
                      : undefined;
                  return (
                    <tr
                      key={o.id}
                      className="hover:bg-accent/50 transition-colors"
                      style={zebraStyle}
                    >
                      <Td className="text-xs">
                        {label ? (
                          <span className="font-medium">{label}</span>
                        ) : (
                          <span
                            className="text-muted-foreground font-mono"
                            title={o.assetId}
                          >
                            {o.assetId.slice(0, 12)}…
                          </span>
                        )}
                      </Td>
                      <Td className="font-mono text-xs">{String(o.side).toUpperCase()}</Td>
                      <Td className="text-right font-mono text-xs">{remaining.toLocaleString()}</Td>
                      <Td className="text-right font-mono text-xs">
                        {Number.isFinite(o.price) ? Number(o.price).toFixed(4) : '—'}
                      </Td>
                      <Td className="text-right font-mono text-xs">
                        <Money
                          value={valueDollars}
                          className="text-xs"
                        />
                      </Td>
                      {onCancelPolyOrder && (
                        <Td className="text-center">
                          <button
                            onClick={() => onCancelPolyOrder(o.id)}
                            className="hover:bg-destructive/20 hover:text-destructive rounded p-1 transition-colors"
                            title="Cancel Polymarket order"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </Td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Show more/less */}
      {totalOrders > maxRows && (
        <div className="border-border/50 flex items-center justify-center border-t p-2">
          <button
            type="button"
            onClick={() => setShowAllRows((v) => !v)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/30 flex items-center gap-2 rounded px-3 py-1.5 text-xs transition-colors"
            aria-expanded={showAllRows}
            aria-label={showAllRows ? 'Show fewer orders' : 'Show more orders'}
          >
            <span className="tabular-nums">
              {showAllRows
                ? `Showing ${totalOrders}`
                : `Showing ${Math.min(maxRows, totalOrders)} of ${totalOrders}`}
            </span>
            {showAllRows ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

export default LegacyOpenOrdersPanel;
